import {DB} from '@/store/kysely_types';
import {Kysely} from 'kysely';
import {DBObjects} from '@/store/store_types';

type Props = {
    db: Kysely<DB>;
    getFileSrc: (file: DBObjects['File'] & {full_path?: string}) => Promise<string>;
}

export default async function (props: Props) {
    await runMigration(props);
}

const fetchWithRetries = async (url: string) => {
    for (let i = 0; i < 10; i++) {
        try {
            const response = await fetch(url);
            if (response.ok) {
                return response;
            }
        } catch (e) {
            console.error('Failed to fetch file. Retrying', url, e);
        }
    }
    throw new Error('Failed to fetch file');
};

const runMigration = async (props: Props) => {
    const db = props.db;
    const getFileSrc = props.getFileSrc as (file: {full_path: string}) => Promise<string>;
    const workspaces = await getAllWorkspaces({db});

    for (const workspace of workspaces) {
        const files = await getRawFilesForWorkspace({db, workspace_id: workspace.id});

        for (const file of files) {
            try {
                if (file.hash) {
                    continue;
                }

                const fileSrc = await getFileSrc(file);
                const response = await fetchWithRetries(fileSrc);
                const fileBlob = await response.blob();
                const hashAndSize = await browserComputeSha1(fileBlob);
                await updateRawFileHash({
                    db,
                    rawFileId: file.id,
                    hash: hashAndSize.hash,
                    size: hashAndSize.size,
                });
            } catch (e) {
                console.error('Failed to backfill hash for file', file.id, e);
            }
        }
    }
};

const getAllWorkspaces = async (args: {
    db: Kysely<DB>;
}) => {
    const db = args.db;

    const workspaces = await db.selectFrom('Workspace')
        .selectAll('Workspace')
        .execute();

    return workspaces;
};

const getRawFilesForWorkspace = async (args: {
    db: Kysely<DB>;
    workspace_id: string;
}) => {
    const db = args.db;

    const files = await db.selectFrom('RawFile')
        .selectAll()
        .where('workspace_id', '=', args.workspace_id)
        .execute();

    return files;
};

const updateRawFileHash = async (args: {
    db: Kysely<DB>;
    rawFileId: string;
    hash: string;
    size: number;
}) => {
    const db = args.db;

    await db
        .updateTable('RawFile')
        .set({
            hash: args.hash,
            size: args.size,
        })
        .where('id', '=', args.rawFileId)
        .execute();
};

import {sha1} from 'js-sha1';

async function browserComputeSha1(file: File | Blob) {
    const hash = sha1.create();
    const chunkSize = 1024 * 1024; // 1MB
    const totalSize = file.size;
    let offset = 0;

    while (offset < totalSize) {
        const chunk = file.slice(offset, offset + chunkSize);
        const buffer = new Uint8Array(await chunk.arrayBuffer());
        hash.update(buffer);
        offset += chunkSize;
    }

    return {
        hash: hash.hex(),
        size: file.size,
    }
}
